import { useState, MouseEvent, ChangeEvent } from 'react'
import { TransformedSites } from './types'

type DeleteMode = 'single' | 'bulk' | null

interface UseSitesTableActionsHandlersProps {
  onEditClick: (siteId: string) => void
  onDeleteSite: (siteId: string) => void
  onBulkDeleteSites: (siteIds: Array<string>) => void
  sites: TransformedSites | undefined
}

export default function useSitesTableActionsHandlers({
  onEditClick,
  onDeleteSite,
  onBulkDeleteSites,
  sites,
}: UseSitesTableActionsHandlersProps) {
  const [selected, setSelected] = useState<string[]>([])
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState<DeleteMode>(null)

  const handleEditClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (selectedSiteId) {
      onEditClick(selectedSiteId)
    }
  }

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(sites?.items.map((row) => row.id) ?? [])
    } else {
      setSelected([])
    }
  }
  const handleClick = (_event: MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id)
    const newSelected = selectedIndex === -1 ? [...selected, id] : selected.filter((selectedId) => selectedId !== id)
    setSelected(newSelected)
  }

  const handleOpenMenu = (event: MouseEvent<HTMLElement>, id: string) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
    setSelectedSiteId(id)
  }

  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
  }

  const handleDeleteSite = () => {
    if (selectedSiteId) {
      setMenuAnchorEl(null)
      setDeleteMode('single')
      setConfirmDialogOpen(true)
    }
  }

  const handleBulkDeleteSites = () => {
    if (selected.length) {
      setDeleteMode('bulk')
      setConfirmDialogOpen(true)
    }
  }

  const resetDeleteConfirmation = () => {
    setConfirmDialogOpen(false)
    setDeleteMode(null)
    setSelectedSiteId(null)
    setMenuAnchorEl(null)
  }

  const handleConfirmDelete = () => {
    if (deleteMode === 'single' && selectedSiteId) {
      onDeleteSite(selectedSiteId)
    } else if (deleteMode === 'bulk' && selected.length) {
      onBulkDeleteSites(selected)
      setSelected([])
    }
    resetDeleteConfirmation()
  }

  const handleCancelDelete = () => {
    resetDeleteConfirmation()
  }

  const isPluralDelete = deleteMode === 'bulk' && selected.length > 1
  const confirmDialogTitle = isPluralDelete ? 'Delete Sites' : 'Delete Site'
  const confirmDialogDescription = isPluralDelete
    ? 'This will permanently delete the selected sites and cannot be undone.'
    : 'This will permanently delete the site and cannot be undone.'

  return {
    selected,
    menuAnchorEl,
    selectedSiteId,
    confirmDialogOpen,
    confirmDialogTitle,
    confirmDialogDescription,
    handleEditClick,
    handleSelectAllClick,
    handleClick,
    handleOpenMenu,
    handleCloseMenu,
    handleDeleteSite,
    handleBulkDeleteSites,
    handleConfirmDelete,
    handleCancelDelete,
  }
}
